import React from 'react';
import { Link } from 'react-router';
import { I18n as I } from 'react-redux-i18n';

import { AUCTION_WARNING_EXPIRING_HOURS } from 'lib/Constants';
import { AuctionStatuses, isNil, lpad, getCountdown } from 'vdlib';
import LookupsHelper from 'lib/LookupsHelper';

const statusData = {
  [AuctionStatuses.Pending]: {
    className: 'default',
    title: I.t('AUCTION_STATUS_PENDING'),
  },
  [AuctionStatuses.Active]: {
    className: 'success',
    title: I.t('AUCTION_STATUS_FINISHING_IN'),
  },
  [AuctionStatuses.ForSelection]: {
    className: 'warning',
    title: I.t('AUCTION_STATUS_OVERDUE'),
  },
  [AuctionStatuses.Deleted]: {
    className: 'danger',
    title: I.t('AUCTION_STATUS_DELETED'),
  },
  [AuctionStatuses.Cancelled]: {
    className: 'danger',
    title: I.t('AUCTION_STATUS_CANCELED'),
  },
  [AuctionStatuses.Finished]: {
    className: 'default',
    title: I.t('AUCTION_STATUS_FINISHED'),
  },
  [AuctionStatuses.ForAcceptance]: {
    className: 'info',
    title: I.t('AUCTION_STATUS_FOR_ACCEPTANCE'),
  },
  [AuctionStatuses.ForDispatch]: {
    className: 'danger',
    title: I.t('AUCTION_STATUS_FOR_DELIVERY'),
  },
};

const AuctionStatusCellRenderer = ({ data }) => {
  const [countdown, setCountdown] = React.useState(false);

  const status = React.useMemo(
    () => LookupsHelper.getDescription('auctionStatuses', data.StatusID),
    [data]
  );

  const interval = React.useRef(null);

  const extractCountdown = React.useCallback(() => {
    let countdown = getCountdown(data.EndDate);
    return isNil(countdown) || countdown.done ? false : countdown;
  }, [data]);

  React.useEffect(() => {
    setCountdown(extractCountdown());

    clearInterval(interval.current);
    interval.current = null;

    if (status === AuctionStatuses.Active && countdown !== false) {
      interval.current = setInterval(() => {
        const countdown = extractCountdown();
        if (countdown === false) clearInterval(interval.current);
        setCountdown(countdown);
      }, 1000);
    }
  }, [data]); //eslint-disable-line

  // cleanup effect
  React.useEffect(() => {
    return () => {
      clearInterval(interval.current);
    };
  }, []);

  const { className, content, title } = React.useMemo(() => {
    const hasCountdown = !!countdown && !countdown.done;
    const expiresToday = hasCountdown && countdown.days === 0;
    const expiresTomorrow = hasCountdown && countdown.days === 1;

    let { className, title } = statusData[status] || {};

    if (expiresToday && countdown.hours <= AUCTION_WARNING_EXPIRING_HOURS)
      className = 'warning';
    else if (!hasCountdown) className = 'danger';

    if (expiresTomorrow) title = (title || '').split(' ')[0] + ' ';
    else if (!hasCountdown) title = 'Έληξε';

    const content = !hasCountdown ? null : expiresToday ? (
      <span>
        {lpad(countdown.days * 24 + countdown.hours, 2, '0')} :
        {lpad(countdown.minutes, 2, '0')} :{lpad(countdown.seconds, 2, '0')}
      </span>
    ) : expiresTomorrow ? (
      I.t('TOMORROW')
    ) : (
      `${countdown.days} ${I.t('DAYS')}`
    );

    return { className, content, title };
  }, [countdown, status]);

  return (
    <Link to={`/auctions/${data.ID}`}>
      <span className={`label-sm label label-${className}`}>
        {title}
        {content}
      </span>
    </Link>
  );
};

export default AuctionStatusCellRenderer;
